/**
 * Унифицированный хук для расчета стоимости аренды и заказов
 * Поддерживает:
 * - Почасовую/суточную аренду инвентаря
 * - Покупку товаров магазина (без учета времени)
 * - Комплекты (kits) с почасовой/суточной арендой
 * - Скидки
 * - Смешанные заказы (аренда + покупка)
 */
import { useMemo } from 'react';

// Базовые типы для расчетов
export interface InventoryItemForCalculation {
  id: string;
  name?: string;
  dailyPrice?: number;
  hourlyPrice?: number;
  price?: number; // fallback для старых данных
  price_per_day?: number; // альтернативное поле
}

export interface StoreItemForCalculation {
  id: string;
  name?: string;
  price?: number;
}

export interface KitForCalculation {
  id: string;
  name?: string;
  price?: number;
}

export interface SelectedInventoryItem {
  id: string;
  quantity: number;
}

export interface SelectedStoreItem {
  id: string;
  quantity: number;
}

export interface UseEntityCalculationsProps {
  // Временные рамки (для аренды)
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  
  // Инвентарь для аренды (с учетом времени)
  inventoryItems?: InventoryItemForCalculation[];
  selectedInventoryItems?: SelectedInventoryItem[];
  
  // Товары магазина (без учета времени)
  storeItems?: StoreItemForCalculation[];
  selectedStoreItems?: SelectedStoreItem[];
  
  // Комплекты (с учетом времени)
  kits?: KitForCalculation[];
  selectedKitIds?: string[];
  
  // Скидка в процентах
  discount?: number;
  
  // Режим расчета
  mode?: 'rental' | 'order' | 'mixed'; // rental - только аренда, order - только покупка, mixed - смешанный
  
  // Тип тарифа (для выбора правильной цены)
  tariffType?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
}

export function useEntityCalculations({
  startDate,
  endDate,
  inventoryItems = [],
  selectedInventoryItems = [],
  storeItems = [],
  selectedStoreItems = [],
  kits = [],
  selectedKitIds = [],
  discount = 0,
  mode = 'mixed',
  tariffType = 'daily', // по умолчанию дневной тариф
}: UseEntityCalculationsProps) {
  
  const totalAmount = useMemo(() => {
    let total = 0;
    
    // Расчет времени аренды
    const { fullDays, remainingHours } = calculateRentalTime(startDate, endDate);
    
    // Расчет стоимости инвентаря (аренда с учетом времени)
    if (mode !== 'order') {
      const inventorySubtotal = calculateInventorySubtotal(
        inventoryItems,
        selectedInventoryItems,
        fullDays,
        remainingHours,
        tariffType
      );
      total += inventorySubtotal;
    }
    
    // Расчет стоимости товаров магазина (покупка без учета времени)
    if (mode !== 'rental') {
      const storeSubtotal = calculateStoreSubtotal(storeItems, selectedStoreItems);
      total += storeSubtotal;
    }
    
    // Расчет стоимости комплектов (аренда с учетом времени)
    if (mode !== 'order') {
      const kitsSubtotal = calculateKitsSubtotal(kits, selectedKitIds, fullDays, remainingHours);
      total += kitsSubtotal;
    }
    
    // Применение скидки
    if (discount > 0 && discount <= 100) {
      const discountAmount = total * (discount / 100);
      total = total - discountAmount;
    }
    
    return Math.round(total * 100) / 100; // Округляем до 2 знаков после запятой
  }, [
    startDate,
    endDate,
    inventoryItems,
    selectedInventoryItems,
    storeItems,
    selectedStoreItems,
    kits,
    selectedKitIds,
    discount,
    mode
  ]);
  
  const breakdown = useMemo(() => {
    const { fullDays, remainingHours } = calculateRentalTime(startDate, endDate);
    
    const inventorySubtotal = mode !== 'order' 
      ? calculateInventorySubtotal(inventoryItems, selectedInventoryItems, fullDays, remainingHours, tariffType)
      : 0;
      
    const storeSubtotal = mode !== 'rental'
      ? calculateStoreSubtotal(storeItems, selectedStoreItems)
      : 0;
      
    const kitsSubtotal = mode !== 'order'
      ? calculateKitsSubtotal(kits, selectedKitIds, fullDays, remainingHours)
      : 0;
    
    const subtotal = inventorySubtotal + storeSubtotal + kitsSubtotal;
    const discountAmount = discount > 0 && discount <= 100 ? subtotal * (discount / 100) : 0;
    
    return {
      inventorySubtotal,
      storeSubtotal,
      kitsSubtotal,
      subtotal,
      discountAmount,
      total: Math.round((subtotal - discountAmount) * 100) / 100,
      rentalDays: fullDays,
      rentalHours: remainingHours
    };
  }, [
    startDate,
    endDate,
    inventoryItems,
    selectedInventoryItems,
    storeItems,
    selectedStoreItems,
    kits,
    selectedKitIds,
    discount,
    mode
  ]);

  return {
    totalAmount,
    breakdown
  };
}

/**
 * Расчет времени аренды в днях и часах
 */
function calculateRentalTime(startDate?: Date | string | null, endDate?: Date | string | null) {
  // CRITICAL FIX: Better handling of missing dates for orders
  if (!startDate || !endDate) {
    return { fullDays: 1, remainingHours: 0 };
  }

  try {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { fullDays: 1, remainingHours: 0 };
    }
    
    if (end <= start) {
      return { fullDays: 1, remainingHours: 0 };
    }
    
    const diffMs = end.getTime() - start.getTime();
    const hoursTotal = diffMs / (1000 * 60 * 60);
    
    // CRITICAL FIX: Ensure minimum rental period
    if (hoursTotal < 1) {
      return { fullDays: 1, remainingHours: 0 };
    }
    
    // Разложение на полные сутки и остаток часов
    const fullDays = Math.floor(hoursTotal / 24);
    const remainingHoursRaw = hoursTotal - fullDays * 24;
    const remainingHours = Math.ceil(remainingHoursRaw - 1e-9); // защита от плавающей точки
    
    const result = { 
      fullDays: Math.max(0, fullDays), 
      remainingHours: Math.max(0, remainingHours) 
    };
    
    // CRITICAL FIX: Ensure at least some time period
    if (result.fullDays === 0 && result.remainingHours === 0) {
      return { fullDays: 1, remainingHours: 0 };
    }
    
    return result;
  } catch (error) {
    return { fullDays: 1, remainingHours: 0 };
  }
}

/**
 * Расчет стоимости инвентаря (аренда с учетом времени)
 */
function calculateInventorySubtotal(
  inventoryItems: InventoryItemForCalculation[],
  selectedItems: SelectedInventoryItem[],
  fullDays: number,
  remainingHours: number,
  tariffType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom' = 'daily'
): number {
  return selectedItems.reduce((sum, selectedItem) => {
    const item = inventoryItems.find(inv => inv.id === selectedItem.id);
    if (!item) {
      return sum;
    }
    
    // CRITICAL FIX: Improved price field resolution with better fallbacks
    // Priority: dailyPrice > price_per_day > price > 0
    let dailyPrice = 0;
    if (typeof item.dailyPrice === 'number' && item.dailyPrice > 0) {
      dailyPrice = item.dailyPrice;
    } else if (typeof item.price_per_day === 'number' && item.price_per_day > 0) {
      dailyPrice = item.price_per_day;
    } else if (typeof item.price === 'number' && item.price > 0) {
      dailyPrice = item.price;
    }
    
    // Priority: hourlyPrice > calculated from dailyPrice > 0
    let hourlyPrice = 0;
    if (typeof item.hourlyPrice === 'number' && item.hourlyPrice > 0) {
      hourlyPrice = item.hourlyPrice;
    } else if (dailyPrice > 0) {
      hourlyPrice = dailyPrice / 24;
    }
    
    // CRITICAL FIX: Ensure we have valid time periods (minimum 1 day if no time specified)
    // For orders with inventory items, ensure minimum 1 day rental period
    const hasAnyTime = fullDays > 0 || remainingHours > 0;
    // ИСПРАВЛЕНИЕ: Не добавляем день если есть только часы
    const effectiveFullDays = fullDays;
    const effectiveRemainingHours = remainingHours;
    
    // Если вообще нет времени (и дней, и часов = 0), используем минимум 1 день
    const shouldUseMinimumDay = !hasAnyTime;
    const finalFullDays = shouldUseMinimumDay ? 1 : effectiveFullDays;
    const finalRemainingHours = shouldUseMinimumDay ? 0 : effectiveRemainingHours;
    
    // ИСПРАВЛЕНИЕ: Выбор цены в зависимости от тарифа
    let itemCost = 0;
    if (tariffType === 'hourly') {
      // Для часового тарифа используем только hourlyPrice
      const totalHours = finalFullDays * 24 + finalRemainingHours;
      itemCost = hourlyPrice * totalHours * selectedItem.quantity;
    } else {
      // Для дневного и других тарифов используем dailyPrice + остаток часов
      itemCost = (dailyPrice * finalFullDays + hourlyPrice * finalRemainingHours) * selectedItem.quantity;
    }
    
    return sum + itemCost;
  }, 0);
}

/**
 * Расчет стоимости товаров магазина (покупка без учета времени)
 */
function calculateStoreSubtotal(
  storeItems: StoreItemForCalculation[],
  selectedItems: SelectedStoreItem[]
): number {
  return selectedItems.reduce((sum, selectedItem) => {
    const item = storeItems.find(store => store.id === selectedItem.id);
    if (!item || !item.price) return sum;
    
    return sum + item.price * selectedItem.quantity;
  }, 0);
}

/**
 * Расчет стоимости комплектов (аренда с учетом времени)
 */
function calculateKitsSubtotal(
  kits: KitForCalculation[],
  selectedKitIds: string[],
  fullDays: number,
  remainingHours: number
): number {
  return selectedKitIds.reduce((sum, kitId) => {
    const kit = kits.find(k => k.id === kitId);
    if (!kit || !kit.price) return sum;
    
    // Комплекты считаем как суточную аренду
    const dailyPrice = kit.price;
    const hourlyPrice = dailyPrice / 24;
    const kitCost = dailyPrice * fullDays + hourlyPrice * remainingHours;
    
    return sum + kitCost;
  }, 0);
}
